import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

const PHASE_PART = '.investment-phase-header, .investment-total-node-view, .tableWrapper, table';
const SELECTION_CONTROL =
  '[data-investment-inspector], [data-workspace-controls], [data-editor-toolbar], [role="dialog"], [role="menu"], [role="listbox"]';

function investmentContainer(view: EditorView, pos: number) {
  const dom = view.nodeDOM(pos);
  const container = dom instanceof HTMLElement
    ? dom.matches('.investment-group-view') ? dom : dom.querySelector('.investment-group-view')
    : null;
  return container instanceof HTMLElement ? container : null;
}

/** Kop, tabel en totaal zijn losse nodes, maar vormen samen één selecteerbaar overzicht. */
function selectedPhase(view: EditorView) {
  const selection = view.state.selection;
  if (selection instanceof NodeSelection && selection.node.type.name === 'investmentGroupNode') {
    const container = investmentContainer(view, selection.from);
    return container
      ? { container, parts: [container], tableId: String(selection.node.attrs.id) }
      : null;
  }
  const head = selection instanceof NodeSelection ? selection.from : selection.head;
  const $head = view.state.doc.resolve(head);
  for (let depth = $head.depth; depth > 0; depth--) {
    const group = $head.node(depth);
    if (group.attrs.investmentProduct) return null;
    if (group.type.name !== 'investmentGroupNode') continue;

    const groupPos = $head.before(depth);
    const parts: HTMLElement[] = [];
    let tableId: string | null = null;
    let from = 0;
    let to = 0;
    let ended = false;
    group.forEach((child, offset) => {
      if (ended) return;
      const pos = groupPos + 1 + offset;
      if (child.type.name === 'investmentHeaderNode') {
        if (head < pos) {
          ended = true;
          return;
        }
        tableId = child.attrs.isMainHeader ? null : String(child.attrs.tableId ?? '');
        from = pos;
        parts.length = 0;
      }
      if (!tableId || head < from) return;
      to = pos + child.nodeSize;
      const dom = view.nodeDOM(pos);
      if (dom instanceof HTMLElement) parts.push(dom);
    });
    if (!tableId || head < from || head >= to || !parts.length) return null;

    const container = investmentContainer(view, groupPos);
    return container ? { container, parts, tableId } : null;
  }
  return null;
}

export const InvestmentSelection = Extension.create({
  name: 'investmentSelection',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        view: (view) => {
          const outline = document.createElement('div');
          outline.className = 'investment-selection-outline';
          outline.contentEditable = 'false';
          outline.setAttribute('aria-hidden', 'true');
          outline.dataset.html2canvasIgnore = 'true';
          let active = view.hasFocus();
          let frame = 0;
          let observed: HTMLElement | null = null;
          let destroyed = false;

          const render = () => {
            frame = 0;
            const activeSelection = view.dom.dataset.quoteWorkspace === 'preview'
              ? view.dom.dataset.workspaceSelection === 'active'
              : active && view.editable;
            const phase = activeSelection ? selectedPhase(view) : null;
            if (!phase) {
              outline.remove();
              resizeObserver.disconnect();
              observed = null;
              return;
            }

            // TableKit kan display:contents gebruiken; meet dan de zichtbare rijen.
            const rects = phase.parts
              .flatMap((part) => {
                const rect = part.getBoundingClientRect();
                return rect.width && rect.height
                  ? [rect]
                  : Array.from(part.querySelectorAll('tr'), (row) => row.getBoundingClientRect());
              })
              .filter((rect) => rect.width && rect.height);
            if (!rects.length) {
              outline.remove();
              return;
            }
            const containerRect = phase.container.getBoundingClientRect();
            const scale = containerRect.width / phase.container.offsetWidth;
            if (!scale) return;
            const left = Math.min(...rects.map((rect) => rect.left));
            const top = Math.min(...rects.map((rect) => rect.top));
            const right = Math.max(...rects.map((rect) => rect.right));
            const bottom = Math.max(...rects.map((rect) => rect.bottom));
            // Keep a whole-page border inside the page, including at narrow preview widths.
            const gutter = phase.parts[0] === phase.container ? 0 : 8;
            Object.assign(outline.style, {
              left: `${(left - containerRect.left) / scale - gutter}px`,
              top: `${(top - containerRect.top) / scale - gutter}px`,
              width: `${(right - left) / scale + gutter * 2}px`,
              height: `${(bottom - top) / scale + gutter * 2}px`,
            });
            outline.dataset.investmentSelected = phase.tableId;
            if (outline.parentElement !== phase.container) phase.container.append(outline);
            if (observed !== phase.container) {
              resizeObserver.disconnect();
              resizeObserver.observe(phase.container);
              observed = phase.container;
            }
          };
          const schedule = () => {
            if (!destroyed && !frame) frame = requestAnimationFrame(render);
          };
          const resizeObserver = new ResizeObserver(schedule);

          // Inline inputs en knoppen in atom-nodeviews wijzigen de PM-selectie niet zelf.
          const selectInlineNode = (target: Element) => {
            const atom = target.closest('.investment-phase-header, .investment-total-node-view');
            if (!atom) return;
            const wrapper = atom.closest('.react-renderer');
            if (!wrapper?.parentNode) return;
            const pos = view.posAtDOM(
              wrapper.parentNode,
              Array.from(wrapper.parentNode.childNodes).indexOf(wrapper),
              -1
            );
            const node = view.state.doc.nodeAt(pos);
            if (
              node?.type.name !== 'investmentHeaderNode' &&
              node?.type.name !== 'investmentTotalNode'
            )
              return;
            const selection = NodeSelection.create(view.state.doc, pos);
            if (!selection.eq(view.state.selection))
              view.dispatch(view.state.tr.setSelection(selection));
          };
          const onPointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (
              !(target instanceof Element) ||
              (!view.editable && view.dom.dataset.quoteWorkspace !== 'preview')
            )
              return;
            if (target.closest(SELECTION_CONTROL)) return;
            active =
              view.dom.contains(target) &&
              !!target.closest(PHASE_PART) &&
              !target.closest('.investment-rate-card');
            if (active) {
              view.dom.dataset.workspaceSelection = 'active';
              selectInlineNode(target);
            }
            else outline.remove();
            schedule();
          };
          const onFocusIn = (event: FocusEvent) => {
            const target = event.target;
            if (
              !(target instanceof Element) ||
              (!view.editable && view.dom.dataset.quoteWorkspace !== 'preview')
            )
              return;
            if (target.closest(SELECTION_CONTROL)) return;
            if (view.dom.contains(target)) {
              if (target.closest(PHASE_PART)) {
                active = true;
                selectInlineNode(target);
              }
            } else {
              active = false;
              outline.remove();
            }
            schedule();
          };
          const onKeyDown = (event: KeyboardEvent) => {
            if (!(event.target instanceof Element) || !view.dom.contains(event.target)) return;
            active = event.key !== 'Escape';
            if (!active) outline.remove();
            schedule();
          };

          document.addEventListener('pointerdown', onPointerDown, true);
          document.addEventListener('focusin', onFocusIn, true);
          view.dom.addEventListener('keydown', onKeyDown);
          window.addEventListener('resize', schedule);
          void document.fonts.ready.then(schedule);
          schedule();
          return {
            update: schedule,
            destroy: () => {
              destroyed = true;
              document.removeEventListener('pointerdown', onPointerDown, true);
              document.removeEventListener('focusin', onFocusIn, true);
              view.dom.removeEventListener('keydown', onKeyDown);
              window.removeEventListener('resize', schedule);
              resizeObserver.disconnect();
              cancelAnimationFrame(frame);
              outline.remove();
            },
          };
        },
      }),
    ];
  },
});
